// Single source of truth for the user's display currency.
//
// Product prices always keep their ORIGINAL currency as received from the
// catalog / Bokun. Nothing here mutates catalog data — conversion happens only
// at presentation time through `format()` / `convert()`.
//
// The store is a tiny external store (no provider needed, works with SSR via
// useSyncExternalStore's server snapshot) so every component that renders a
// price re-renders instantly when the currency changes.
import { useCallback, useEffect, useSyncExternalStore } from "react";

export const SUPPORTED_CURRENCIES = ["MXN", "USD", "EUR"] as const;
export type Currency = (typeof SUPPORTED_CURRENCIES)[number];
const SOURCE_CURRENCIES = ["MXN", "USD", "EUR", "GBP", "CAD"] as const;
type SourceCurrency = (typeof SOURCE_CURRENCIES)[number];

export const CURRENCY_META: Record<Currency, { flag: string; label: string }> = {
  MXN: { flag: "🇲🇽", label: "Peso mexicano" },
  USD: { flag: "🇺🇸", label: "US Dollar" },
  EUR: { flag: "🇪🇺", label: "Euro" },
};

export const STORAGE_KEY = "mm.currency";

/** Units of each currency per 1 MXN. Offline fallback, refreshed at runtime. */
export const FALLBACK_RATES: Record<SourceCurrency, number> = {
  MXN: 1,
  USD: 0.055,
  EUR: 0.05,
  GBP: 0.043,
  CAD: 0.075,
};

export function normalizeCurrency(input?: string | null): Currency {
  const code = (input ?? "").trim().toUpperCase();
  if (code === "GBP" || code === "CAD") return "USD";
  return (SUPPORTED_CURRENCIES as readonly string[]).includes(code)
    ? (code as Currency)
    : "MXN";
}

type State = { currency: Currency; rates: Record<string, number> };

let state: State = { currency: "MXN", rates: { ...FALLBACK_RATES } };
const listeners = new Set<() => void>();
const serverSnapshot: State = state;

function emit() {
  for (const l of listeners) l();
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function getSnapshot(): State {
  return state;
}

function getServerSnapshot(): State {
  return serverSnapshot;
}

export function getCurrency(): Currency {
  return state.currency;
}

export function setCurrency(next: Currency) {
  const currency = normalizeCurrency(next);
  if (currency === state.currency) return;
  state = { ...state, currency };
  try {
    localStorage.setItem(STORAGE_KEY, currency);
  } catch { /* storage may be unavailable */ }
  try {
    document.cookie = `mm.currency=${currency}; path=/; max-age=31536000; SameSite=Lax`;
  } catch {
    /* ignore */
  }
  emit();
  window.dispatchEvent(new Event("mm:preferences-changed"));
}

export function setRates(rates: Record<string, number>) {
  const merged: Record<string, number> = { ...FALLBACK_RATES };
  for (const code of SOURCE_CURRENCIES) {
    const value = Number(rates[code]);
    if (Number.isFinite(value) && value > 0) merged[code] = value;
  }
  state = { ...state, rates: merged };
  emit();
}

/** Read persisted preference (client only). Idempotent. */
export function hydrateCurrency() {
  if (typeof window === "undefined") return;
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
  if (!stored) {
    const match = document.cookie.match(/(?:^|;\s*)mm\.currency=([^;]+)/);
    stored = match?.[1] ?? null;
  }
  const currency = normalizeCurrency(stored);
  if (stored && stored !== currency) {
    try { localStorage.setItem(STORAGE_KEY, currency); } catch { /* storage may be unavailable */ }
    try { document.cookie = `mm.currency=${currency}; path=/; max-age=31536000; SameSite=Lax`; } catch { /* ignore */ }
  }
  if (currency !== state.currency) {
    state = { ...state, currency };
    emit();
  }
}

/** Convert an amount between currencies using MXN as the pivot. */
export function convertAmount(
  amount: number,
  from: string | null | undefined,
  to: Currency,
  rates: Record<string, number> = state.rates,
): number {
  // Original prices may still be denominated in a hidden currency.
  const sourceCode = (from ?? "").trim().toUpperCase();
  const source: SourceCurrency = (SOURCE_CURRENCIES as readonly string[]).includes(sourceCode)
    ? (sourceCode as SourceCurrency)
    : "MXN";
  if (source === to) return amount;
  const fromRate = rates[source] ?? FALLBACK_RATES[source];
  const toRate = rates[to] ?? FALLBACK_RATES[to];
  if (!fromRate || !toRate) return amount;
  return (amount / fromRate) * toRate;
}

function localeFor(lang?: string) {
  switch ((lang ?? "es").slice(0, 2)) {
    case "en":
      return "en-US";
    case "fr":
      return "fr-FR";
    case "pt":
      return "pt-BR";
    case "it":
      return "it-IT";
    case "de":
      return "de-DE";
    default:
      return "es-MX";
  }
}

export function formatMoney(
  amount: number | null | undefined,
  currency: Currency,
  opts: { locale?: string; decimals?: number } = {},
): string {
  if (amount === null || amount === undefined || !Number.isFinite(amount)) return "—";
  const decimals = opts.decimals ?? 0;
  try {
    return new Intl.NumberFormat(localeFor(opts.locale), {
      style: "currency",
      currency,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(decimals)}`;
  }
}

/**
 * Centralized presentation helper: takes the product's original amount and
 * currency and renders it in the currency the user selected.
 */
export function formatPriceIn(
  amount: number | null | undefined,
  sourceCurrency: string | null | undefined,
  targetCurrency: Currency,
  opts: { locale?: string; rates?: Record<string, number>; decimals?: number } = {},
): string {
  if (amount === null || amount === undefined) return "—";
  const converted = convertAmount(amount, sourceCurrency, targetCurrency, opts.rates);
  return formatMoney(converted, targetCurrency, opts);
}

/**
 * Reactive access to the selected currency plus a `format` helper.
 * Every component that shows a price should use this.
 */
export function useCurrency() {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const format = useCallback(
    (
      amount: number | null | undefined,
      sourceCurrency: string | null | undefined,
      opts: { locale?: string; decimals?: number } = {},
    ) =>
      formatPriceIn(amount, sourceCurrency, snapshot.currency, {
        ...opts,
        rates: snapshot.rates,
      }),
    [snapshot],
  );

  const convert = useCallback(
    (amount: number, sourceCurrency: string | null | undefined) =>
      convertAmount(amount, sourceCurrency, snapshot.currency, snapshot.rates),
    [snapshot],
  );

  return {
    currency: snapshot.currency,
    rates: snapshot.rates,
    setCurrency,
    format,
    convert,
  };
}

/**
 * Convenience hook: `money(amount, sourceCurrency, decimals?)`.
 * Use it anywhere a raw amount + its original currency must be displayed.
 */
export function useMoney() {
  const { format } = useCurrency();
  return useCallback(
    (amount: number | null | undefined, sourceCurrency?: string | null, decimals = 2) =>
      format(amount, sourceCurrency ?? "MXN", { decimals }),
    [format],
  );
}

let ratesRequested = false;

/** Mount once (root) to restore the preference and refresh FX rates. */
export function useCurrencyBootstrap() {
  useEffect(() => {
    hydrateCurrency();
    if (ratesRequested) return;
    ratesRequested = true;
    void (async () => {
      try {
        const mod = await import("./fx.functions");
        const rates = await mod.getFxRates();
        if (rates) setRates(rates);
      } catch {
        /* keep fallback rates */
      }
    })();
  }, []);
}
